/**
 * Push payload contract: the FCM `data` the API's notify consumer (apps/api/src/push/payload.ts) sends and the apps
 * read (iOS: apps/ios/Shared/PushPayload.swift; Android: FirebaseMessagingService; web: the service worker). FCM data
 * values are always strings; on iOS they arrive as top-level userInfo keys. Apps must tolerate missing keys and
 * ignore unknown ones (new fields are added, never renamed).
 */
import { z } from "zod";
import { ChannelKind } from "./entities.ts";

/**
 * `data.type`: a new message, "read on another device" (silent; clears notifications, sets the badge), or someone
 * joined the workspace through an allowed email domain (owners and admins; apps that don't know it show the
 * notification block FCM carries, or nothing).
 */
export const PushType = z.enum(["message", "read", "member_joined", "huddle"]);
export type PushType = z.infer<typeof PushType>;

/** Decimal integer as a string (FCM data is string-only). */
const IntString = z.string().regex(/^\d+$/);

/** `type: "message"` — iOS shows `aps.alert`; Android renders `title`/`body` itself; web shows `webpush.notification`. */
export const MessagePushData = z.object({
  type: z.literal("message"),
  workspaceId: z.string(),
  workspaceName: z.string(),
  channelId: z.string(),
  /** Empty for DMs and group DMs. */
  channelName: z.string(),
  channelKind: ChannelKind,
  messageId: z.string(),
  /** Channel seq of the message (read pushes clear notifications up to a seq). */
  seq: IntString,
  /** Thread root id, for thread replies only. */
  threadId: z.string().optional(),
  senderId: z.string(),
  senderName: z.string(),
  /** https only. */
  senderAvatarUrl: z.string().optional(),
  /** The signed-in person the push is for. */
  recipientId: z.string(),
  /** Unread mentions across the recipient's workspaces (also `aps.badge` on iOS). None on a channel message's push, which leaves the number alone. */
  badge: IntString.optional(),
  /** Ready-to-show notification text in the device's language (Android renders it; iOS/web get it in alert form). */
  title: z.string(),
  body: z.string(),
  /**
   * The notification sound to play (packages/core logic/notify NOTIFICATION_SOUNDS, "knock" by default). Apps play
   * their bundled copy of it (scripts/sounds/generate.ts makes them), or the platform default for a name they lack.
   */
  sound: z.string().optional(),
});
export type MessagePushData = z.infer<typeof MessagePushData>;

/** `type: "read"` — iOS and Android only (`content-available: 1` on iOS). */
export const ReadPushData = z.object({
  type: z.literal("read"),
  workspaceId: z.string(),
  channelId: z.string(),
  /** Clear this conversation's notifications whose `seq` is ≤ this. */
  seq: IntString,
  badge: IntString,
  recipientId: z.string(),
});
export type ReadPushData = z.infer<typeof ReadPushData>;

/** `type: "member_joined"` — iOS `aps.alert`, Android `android.notification`, web `webpush.notification`. */
export const MemberJoinedPushData = z.object({
  type: z.literal("member_joined"),
  workspaceId: z.string(),
  workspaceName: z.string(),
  /** The person who joined. */
  userId: z.string(),
  domain: z.string(),
  recipientId: z.string(),
  title: z.string(),
  body: z.string(),
});
export type MemberJoinedPushData = z.infer<typeof MemberJoinedPushData>;

/**
 * `type: "huddle"` — someone started a huddle in my DM or rang me into one. iOS `aps.alert`, Android
 * `android.notification` (apps that don't know the type still show it), web `webpush.notification`; a click opens the
 * conversation.
 */
export const HuddlePushData = z.object({
  type: z.literal("huddle"),
  workspaceId: z.string(),
  workspaceName: z.string(),
  channelId: z.string(),
  channelKind: ChannelKind,
  huddleId: z.string(),
  /** Who started it or rang. */
  senderId: z.string(),
  senderName: z.string(),
  recipientId: z.string(),
  title: z.string(),
  body: z.string(),
});
export type HuddlePushData = z.infer<typeof HuddlePushData>;

/**
 * `type: "reminder"` — one of my Later reminders came due (later.ts). iOS `aps.alert`, Android `android.notification`,
 * web `webpush.notification`; a click opens Later (`laterId`).
 */
export const ReminderPushData = z.object({
  type: z.literal("reminder"),
  workspaceId: z.string(),
  workspaceName: z.string(),
  laterId: z.string(),
  /** The saved message's conversation (absent for a text reminder). */
  channelId: z.string().optional(),
  messageId: z.string().optional(),
  recipientId: z.string(),
  title: z.string(),
  body: z.string(),
});
export type ReminderPushData = z.infer<typeof ReminderPushData>;

/**
 * `type: "account"` — a security notice about my account (a phone number was added, changed or removed), to every
 * device of mine, whatever Do not disturb says. A click opens Settings › Account & sign-in.
 */
export const AccountPushData = z.object({
  type: z.literal("account"),
  recipientId: z.string(),
  title: z.string(),
  body: z.string(),
});
export type AccountPushData = z.infer<typeof AccountPushData>;

/**
 * `type: "event_reminder"` — a calendar event of mine starts soon (calendar.ts `reminderMinutes`). Shown like a
 * reminder; a click opens the event in Calendar (`eventId`, `at` = the occurrence's original start, `start` = when it
 * starts, as decimal strings).
 */
export const EventReminderPushData = z.object({
  type: z.literal("event_reminder"),
  workspaceId: z.string(),
  workspaceName: z.string(),
  eventId: z.string(),
  at: IntString,
  start: IntString,
  recipientId: z.string(),
  title: z.string(),
  body: z.string(),
});
export type EventReminderPushData = z.infer<typeof EventReminderPushData>;

/** New mail in one of my mailboxes (a tab I get notified for): a click opens the thread. */
export const MailPushData = z.object({
  type: z.literal("mail"),
  workspaceId: z.string(),
  workspaceName: z.string(),
  mailboxId: z.string(),
  threadId: z.string(),
  messageId: z.string(),
  /** The sender's address. */
  senderId: z.string(),
  senderName: z.string(),
  /** https only. */
  senderAvatarUrl: z.string().optional(),
  recipientId: z.string(),
  title: z.string(),
  body: z.string(),
  /** A verification code in this mail; the notification's Copy action uses it. */
  code: z.string().optional(),
});
export type MailPushData = z.infer<typeof MailPushData>;

/** iOS `aps.category` (and the actions the app registers for it). */
export const PUSH_CATEGORY = {
  /** Channel / DM message: Reply (text input) and Mark read. */
  message: "message",
  /** Thread reply: Mark read. */
  threadReply: "thread-reply",
} as const;
export const PUSH_ACTION = { reply: "reply", markRead: "mark-read" } as const;
